import { FormHelperText, styled } from '@mui/material'
import DialogBase from 'components/dialog/Base'
import { DOC_TYPE, DocType } from 'config'
import { UserGroupConfig, Mode } from 'types'
import { checkFilesErrors } from 'utils/file'
import { isEmpty } from 'utils/utils'

import SettingArea from './SettingArea'
import {
  DocTypeOption,
  AddFilesHandler,
  DeleteFileHandler,
  SelectHandler,
  RadioGroupChangeHandler,
} from './types'
import UploadBox from './UploadBox'

const Container = styled('div')({
  width: 1024,
  maxWidth: '100%',
  overflow: 'hidden',
  display: 'flex',
  minHeight: 400,
})

const LeftBox = styled('div')(({ theme }) => ({
  flex: 4,
  display: 'flex',
  flexDirection: 'column',
  marginRight: theme.spacing(4),
  whiteSpace: 'nowrap',
}))

const RightBox = styled('div')({
  flex: 5,
  overflow: 'auto',
})

interface Props {
  mode: Mode
  onClose: () => void
  onSend: () => void
  uploading: boolean
  open: boolean
  userGroupConfig: UserGroupConfig

  docTypeOptions: DocTypeOption[]
  docType: DocType | null
  onDocTypeSelect: (value: string) => void
  ocrBuilderConfigID: number | null
  onOcrBuilderConfigIDChange: (id: number | null) => void

  group: string
  groupOptions: string[]
  onGroupSelect: SelectHandler
  groupDisabled: boolean

  importType: string
  onImportTypeSelect: RadioGroupChangeHandler
  disableImportType: boolean

  onAddFiles: AddFilesHandler
  onDeleteFile: DeleteFileHandler
  files: (File & { id: number })[]
}
function UploadDialog(props: Props) {
  const {
    mode,
    open,
    onClose,
    uploading,
    docType,
    onDocTypeSelect,
    ocrBuilderConfigID,
    onOcrBuilderConfigIDChange,
    docTypeOptions,
    group,
    groupOptions,
    importType,
    groupDisabled,
    onImportTypeSelect,
    disableImportType,
    onGroupSelect,
    onDeleteFile,
    onAddFiles,
    files,
    onSend,
    userGroupConfig,
  } = props

  const errors = checkFilesErrors(files)
  const readerHelpIconProps = {
    builderPath: '/builder/ja-JP/ocr-reader-doc/read-edit-form.html#Read',
    concurPath: '/concur/ja-JP/ocr-reader-doc/read-file.html',
    invoicePath: '/invoice/ja-JP/ocr-reader-doc/read-edit-form.html#Read',
    orderFormPath: '/order-form/ja-JP/ocr-reader-doc/read-edit-form.html#Read',
  }

  const disableSend =
    !isEmpty(errors) ||
    isEmpty(files) ||
    (docType === DOC_TYPE.OCR_BUILDER && !ocrBuilderConfigID) ||
    isEmpty(group)

  return (
    <DialogBase
      open={open}
      title="新規登録"
      readerHelpIconProps={readerHelpIconProps}
      onClose={onClose}
      loading={uploading}
      align="left"
      cancelText="キャンセル"
      onCancel={onClose}
      actionText="登録"
      onAction={onSend}
      actionProps={{ disabled: disableSend }}
      maxWidth="lg"
      disableScroll
    >
      <Container>
        <LeftBox>
          <UploadBox files={files} onAddFiles={onAddFiles} onDeleteFile={onDeleteFile} />
          {errors.map((errorMessage) => (
            <FormHelperText key={errorMessage} error>
              {errorMessage}
            </FormHelperText>
          ))}
        </LeftBox>
        <RightBox>
          <SettingArea
            mode={mode}
            docTypeOptions={docTypeOptions}
            docType={docType}
            onDocTypeSelect={onDocTypeSelect}
            group={group}
            groupOptions={groupOptions}
            onGroupSelect={onGroupSelect}
            groupDisabled={groupDisabled}
            importType={importType}
            disableImportType={disableImportType}
            onImportTypeSelect={onImportTypeSelect}
            ocrBuilderID={ocrBuilderConfigID}
            onOcrBuilderIDChange={onOcrBuilderConfigIDChange}
            userGroupConfig={userGroupConfig}
          />
        </RightBox>
      </Container>
    </DialogBase>
  )
}

export default UploadDialog
